import { flushCartContext } from './cartContext';
import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './client';
import { mergeNativePages, nativePagePath, nextNativePage, type NativePageCursor } from '@/features/native-support/requestFeed';
import type { NativeRequests } from '@/features/native-support/types';
import { useAuth } from '@/lib/auth/AuthProvider';
import { supabase } from '@/lib/supabase';
import { markTourPending } from '@/lib/tourFlags';
import { markWelcomeVideoPending } from '@/lib/welcomeVideoFlags';
import { normalizeIdentityState } from '@/types/identity';
import type { BootstrapResponse, HomeResponse } from '@/types/bootstrap';
import type {
  AccountCreditResponse,
  Appointment,
  Article,
  ArticleCard,
  LearningResource,
  CancelOrderResponse,
  ClinicalStateResponse,
  Guide,
  GuidedSelfInjectionState,
  MarketplaceResponse,
  MyReferralResponse,
  ReferralRedeemRequest,
  ReferralRedeemResponse,
  ReferralRedemption,
  Order,
  Patient,
  PatientDocument,
  PaymentMethodsResponse,
  PrescriptionCheckoutResponse,
  Protocol,
  ProtocolSummary,
  CareTeamProvider,
  MessagingRouteResponse,
  SendMessageResponse,
  SupportRequestsResponse,
  SetupIntentResponse,
  Slot,
  SupportMessage,
  SupportThread,
  Thread,
  TimezoneSyncResponse,
  TreatmentOffersResponse,
  UpdateAddressBody,
} from '@/types/api';
import type { ActiveExperiment } from '@/lib/experiments';

export { useCartPromotionQuote } from './cartPromotionHooks';

export { useLabAcquisitions, useLabAcquisitionPayment } from './labAcquisitionHooks';

export { useLabCatalog, useLabOrders, useLabOrder, useLabReport, useCreateLabCheckout, useLabCheckoutStatus } from './labHooks';

export function useActiveExperiments() {
  return useQuery({
    queryKey: ['experiments', 'active'],
    queryFn: () => apiFetch<ActiveExperiment[]>('/experiments/active'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Per-user experiment assignment map. Backend-resolved (Phase C of the
 * portal-persistence rollout) — replaces the client-side weighted dice
 * roll in `cohortMissingExperiments`. The endpoint also snapshots the
 * `exp_assignments` cookie on first call so a marketing variant carries
 * through to portal. Audience filter defaults to 'portal'.
 */
export interface MyExperimentAssignmentsResponse {
  assignments: Record<string, string>;
}
export function useMyExperimentAssignments() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['experiments', 'me', 'assignments', session?.user.id],
    enabled: Boolean(session?.user.id),
    queryFn: () =>
      apiFetch<MyExperimentAssignmentsResponse>(
        '/experiments/me/assignments?audience=portal'
      ),
    // Backend computes assignments deterministically and persists them, so
    // the value is stable for the user's lifetime once written. A 5-minute
    // staleTime keeps the network call out of the hot path while still
    // refreshing if a new experiment launches mid-session.
    staleTime: 5 * 60_000,
  });
}

export function useBootstrap() {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => apiFetch<BootstrapResponse>('/me/bootstrap'),
    staleTime: 5 * 60_000,
  });
}

// Patient's own referral code + earnings (GET /me/referral). Backend mints
// on first call and returns 503 if the Stripe-backed service is down — we
// let the card silently hide on error rather than surface the failure.
//
// `enabled` gates the fetch behind the `gift-referrals` experiment: the
// GET has a side effect (it mints a Stripe PromotionCode the first time),
// so we must NOT call it for patients outside the cohort — otherwise the
// flag would only hide the card while still provisioning a code for
// everyone.
export function useMyReferral(enabled = true) {
  return useQuery({
    queryKey: ['me', 'referral'],
    queryFn: () => apiFetch<MyReferralResponse>('/me/referral'),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Spend referral store credit as a Stripe account credit (applies to the next
// renewal or the next new order). Invalidates the referral card so the balance
// refreshes; subscriptions too so any renewal-amount preview re-reads.
export function useRedeemReferralCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReferralRedeemRequest) =>
      apiFetch<ReferralRedeemResponse>('/me/referral/redeem', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'referral'] });
      qc.invalidateQueries({ queryKey: ['me', 'referral', 'redemptions'] });
      qc.invalidateQueries({ queryKey: ['me', 'subscriptions'] });
    },
  });
}

// The patient's redemption history (newest first) for the "Recent
// Redemptions" list. `enabled` gates it behind the experiment like useMyReferral.
export function useMyRedemptions(enabled = true) {
  return useQuery({
    queryKey: ['me', 'referral', 'redemptions'],
    queryFn: () => apiFetch<ReferralRedemption[]>('/me/referral/redemptions'),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// The patient's spendable Stripe account credit (redeemed referral credit).
// Shown in the cart so the credit is visible before they pay. Lightweight —
// the backend reads the Stripe balance directly, no referral-code minting.
export function useAccountCredit(enabled = true) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['me', 'account-credit', session?.user.id],
    queryFn: () => apiFetch<AccountCreditResponse>('/me/account-credit'),
    enabled: enabled && Boolean(session?.user.id),
    staleTime: 30_000,
    retry: false,
  });
}

export function useHome() {
  return useQuery({
    queryKey: ['home'],
    queryFn: () => apiFetch<HomeResponse>('/me/home'),
    staleTime: 60_000,
  });
}

export function useClinicalState() {
  return useQuery({
    queryKey: ['me', 'clinical-state'],
    queryFn: () => apiFetch<ClinicalStateResponse>('/me/clinical-state'),
    staleTime: 30_000,
  });
}

export function useProtocol() {
  return useQuery({
    queryKey: ['me', 'protocol'],
    queryFn: () => apiFetch<Protocol>('/me/protocol'),
    staleTime: 60_000,
  });
}

export function useAppointments() {
  return useQuery({
    queryKey: ['me', 'appointments'],
    queryFn: () => apiFetch<Appointment[]>('/me/appointments'),
    staleTime: 60_000,
  });
}

const guidedSelfInjectionQueryKey = ['me', 'guided-self-injection'] as const;

/**
 * Purchased guided-injection entitlement + lifecycle state. The backend owns
 * both the Sanity experiment gate and injectable-purchase validation; an
 * unentitled patient receives a non-rendering state.
 */
export function useGuidedSelfInjection() {
  return useQuery({
    queryKey: guidedSelfInjectionQueryKey,
    queryFn: () => apiFetch<GuidedSelfInjectionState>('/me/guided-self-injection'),
    staleTime: 30_000,
    retry: false,
  });
}

export function useGuidedSelfInjectionAvailability({
  days = 28,
  enabled = true,
}: { days?: number; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...guidedSelfInjectionQueryKey, 'availability', days],
    queryFn: () => apiFetch<Slot[]>(`/me/guided-self-injection/availability?days=${days}`),
    enabled,
    staleTime: 2 * 60_000,
  });
}

function invalidateGuidedSelfInjection(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: guidedSelfInjectionQueryKey });
  qc.invalidateQueries({ queryKey: ['me', 'appointments'] });
  qc.invalidateQueries({ queryKey: ['home'] });
}

export function useBookGuidedSelfInjection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slot_id: string }) =>
      apiFetch<Appointment>('/me/guided-self-injection/appointments', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateGuidedSelfInjection(qc),
  });
}

export function useRescheduleGuidedSelfInjection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, datetime }: { appointmentId: string; datetime: string }) =>
      apiFetch<Appointment>(`/me/guided-self-injection/appointments/${appointmentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ datetime }),
      }),
    onSuccess: () => invalidateGuidedSelfInjection(qc),
  });
}

export function useCancelGuidedSelfInjection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appointmentId: string) =>
      apiFetch<void>(`/me/guided-self-injection/appointments/${appointmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateGuidedSelfInjection(qc),
  });
}

export type BookingKind = 'default' | 'peptide_consult';

export function useAvailability({
  days = 14,
  kind = 'default',
}: { days?: number; kind?: BookingKind } = {}) {
  // `kind=peptide_consult` queries the dedicated Healthie appointment
  // type (the evaluation-upsell visit) so slot durations + booking
  // subtraction match what actually gets booked.
  return useQuery({
    queryKey: ['me', 'appointments', 'availability', days, kind],
    queryFn: () =>
      apiFetch<Slot[]>(
        `/me/appointments/availability?days=${days}&kind=${kind}`
      ),
    staleTime: 2 * 60_000,
  });
}

export function useBookAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      slot_id: string;
      reason: string;
      kind?: BookingKind;
    }) =>
      apiFetch<Appointment>('/me/appointments', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'appointments'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appointmentId: string) =>
      apiFetch<void>(`/me/appointments/${appointmentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'appointments'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useRescheduleAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      datetime,
    }: {
      appointmentId: string;
      datetime: string;
    }) =>
      apiFetch<Appointment>(`/me/appointments/${appointmentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ datetime }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'appointments'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useOrders() {
  return useQuery({
    queryKey: ['me', 'orders'],
    queryFn: ({ signal }) => apiFetch<Order[]>('/me/orders', { signal }),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: ['me', 'orders', orderId],
    queryFn: ({ signal }) => apiFetch<Order>(`/me/orders/${orderId}`, { signal }),
    enabled: Boolean(orderId),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function usePrescriptionCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (medicationId: string) =>
      apiFetch<PrescriptionCheckoutResponse>(
        `/me/prescriptions/${encodeURIComponent(medicationId)}/checkout`,
        { method: 'POST' }
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] });
      qc.invalidateQueries({ queryKey: ['me', 'orders'] });
      window.location.href = res.url;
    },
  });
}

export function usePrescriptionBundleCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (medicationIds: string[]) =>
      apiFetch<PrescriptionCheckoutResponse>(
        '/me/prescriptions/checkout-bundle',
        {
          method: 'POST',
          body: JSON.stringify({ medication_ids: medicationIds }),
        }
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] });
      qc.invalidateQueries({ queryKey: ['me', 'orders'] });
      window.location.href = res.url;
    },
  });
}

export function useRequestRefill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { medication_id: string }) =>
      apiFetch<Order>('/me/orders/refill', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'orders'] });
    },
  });
}

export function useDocuments() {
  return useQuery({
    queryKey: ['me', 'documents'],
    queryFn: () => apiFetch<PatientDocument[]>('/me/documents'),
    staleTime: 60_000,
  });
}

export function useIdentityStatus() {
  return useQuery({
    queryKey: ['me', 'identity'],
    queryFn: async () =>
      normalizeIdentityState(await apiFetch<unknown>('/me/identity')),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

// ---------- Consents (legal docs the patient agrees to during onboarding) ----------

export type ConsentDocumentSlug = 'data_privacy' | 'npp' | 'telehealth_consent';

export interface ConsentStatusItem {
  document_slug: ConsentDocumentSlug;
  title: string;
  current_version: number;
  signed_version: number | null;
  signed_at_current: boolean;
}

export interface ConsentStatusResponse {
  items: ConsentStatusItem[];
  all_required_signed: boolean;
}

export function useConsentStatus() {
  return useQuery({
    queryKey: ['me', 'consents', 'status'],
    queryFn: () => apiFetch<ConsentStatusResponse>('/me/consents/status'),
    // Status rarely changes mid-session; freshness for the next mount of
    // IntakeWizard (e.g. after an upsell-driven re-entry) is what matters.
    staleTime: 60_000,
  });
}

export interface AcceptConsentsResponse {
  accepted: Array<{
    consent_id: string;
    document_slug: ConsentDocumentSlug;
    document_version: number;
    has_signed_pdf: boolean;
  }>;
  all_required_signed: boolean;
}

export function useAcceptConsents() {
  const qc = useQueryClient();
  return useMutation({
    onMutate: () => {
      // Read BEFORE invalidation: revised legal terms for existing patients
      // must not replay the new-customer welcome. A partial first signing
      // still qualifies as long as at least one required doc is unsigned.
      const status = qc.getQueryData<ConsentStatusResponse>(['me', 'consents', 'status']);
      const boot = qc.getQueryData<BootstrapResponse>(['bootstrap']);
      return {
        welcomePatientId: status?.items.some((item) => item.signed_version === null)
          ? boot?.patient.id
          : undefined,
      };
    },
    mutationFn: (body: { slugs: ConsentDocumentSlug[]; typed_name: string }) =>
      apiFetch<AcceptConsentsResponse>('/me/consents/accept', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data, _variables, context) => {
      // Backend completes onboarding when the last required consent lands
      // (except scheduling_required — the sync-consult gate owns that).
      // Mirror that into the cached bootstrap SYNCHRONOUSLY: the wizard
      // navigates to /app immediately after this mutation, and the
      // RequireOnboarded guard would otherwise read the stale state and
      // flash /onboarding/welcome until the invalidation refetch lands.
      if (data?.all_required_signed) {
        qc.setQueryData<BootstrapResponse | undefined>(['bootstrap'], (old) =>
          // portal_access_ready is what the guards read (the backend always
          // sends it), so flip it too or /app bounces back to welcome.
          old && old.onboarding_state !== 'scheduling_required'
            ? { ...old, onboarding_state: 'complete', portal_access_ready: true }
            : old
        );
        // Arm the /app guided tour — the first Home visit after onboarding
        // plays the walkthrough (features/tour/GuidedTour). Keyed per
        // patient; no-op if already completed on this device.
        const boot = qc.getQueryData<BootstrapResponse | undefined>([
          'bootstrap',
        ]);
        if (boot?.patient.id) {
          if (context?.welcomePatientId === boot.patient.id) {
            markWelcomeVideoPending(boot.patient.id);
          }
          markTourPending(boot.patient.id);
        }
      }
      qc.invalidateQueries({ queryKey: ['me', 'consents', 'status'] });
      qc.invalidateQueries({ queryKey: ['me', 'onboarding'] });
      qc.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

// Public templates — the consent screen's "read the document" links resolve
// through these (each returns a short-lived signed URL). No auth required
// on the backend.
export async function fetchLegalDocumentUrl(
  slug: ConsentDocumentSlug
): Promise<string> {
  const res = await apiFetch<{ url: string; expires_in: number }>(
    `/legal/documents/${slug}`
  );
  return res.url;
}

interface UploadUrlOut {
  file_upload_id: string;
  storage_key: string;
  upload_url: string;
  upload_token: string | null;
  complete_url: string;
}

/**
 * Two-step document upload:
 *   1. POST /me/documents/upload-url — backend creates pending file_uploads
 *      row and mints a Supabase Storage signed PUT URL.
 *   2. PUT bytes directly to that URL — never touches our backend, so
 *      Railway bandwidth stays flat.
 *   3. POST /me/documents/{id}/complete — backend pulls bytes back from
 *      Storage, base64-encodes, calls Healthie createDocument, marks ready.
 *
 * Hook returns a `mutate(file)` you can call from a file input.
 */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      purpose = 'other',
    }: {
      file: File;
      purpose?: string;
    }) => {
      const meta = await apiFetch<UploadUrlOut>('/me/documents/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          filename: file.name,
          mime: file.type || 'application/octet-stream',
          size_bytes: file.size,
          purpose,
        }),
      });
      // Direct PUT to Supabase Storage. Don't use apiFetch — that prepends
      // our API base URL and adds the Bearer header, both of which break
      // Storage's signed URL.
      const putRes = await fetch(meta.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          // Supabase Storage signed-PUT expects this header on writes. Some
          // servers 400 without it.
          'x-upsert': 'false',
        },
      });
      if (!putRes.ok) {
        let detail = '';
        try {
          detail = await putRes.text();
        } catch {
          /* ignore */
        }
        throw new Error(
          `Storage PUT failed: ${putRes.status}${detail ? ' — ' + detail.slice(0, 200) : ''}`
        );
      }
      return apiFetch<{
        file_upload_id: string;
        emr_document_id: string | null;
        status: string;
        id_check_status?: string | null;
        feedback?: string | null;
      }>(`/me/documents/${meta.file_upload_id}/complete`, { method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'documents'] });
      qc.invalidateQueries({ queryKey: ['me', 'identity'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useThreads() {
  return useQuery({
    queryKey: ['me', 'threads'],
    queryFn: () => apiFetch<Thread[]>('/me/threads'),
    staleTime: 30_000,
  });
}

export function useThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ['me', 'threads', threadId],
    queryFn: () => apiFetch<Thread>(`/me/threads/${threadId}`),
    enabled: Boolean(threadId),
    // Replies land out-of-band (clinician in Healthie, or the support AI
    // ~20-40s after the patient's message) — poll tight enough that the
    // answer appears while the patient is still looking at the drawer.
    refetchInterval: 6_000,
  });
}

export function usePostMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string }) =>
      apiFetch<Thread>(`/me/threads/${threadId}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (thread) => {
      qc.setQueryData(['me', 'threads', threadId], thread);
      qc.invalidateQueries({ queryKey: ['me', 'threads'] });
    },
  });
}

export function usePostMessageAttachment(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, body }: { file: File; body?: string }) => {
      const form = new FormData();
      form.append('file', file);
      if (body?.trim()) form.append('body', body.trim());
      return apiFetch<Thread>(`/me/threads/${threadId}/attachments`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: (thread) => {
      qc.setQueryData(['me', 'threads', threadId], thread);
      qc.invalidateQueries({ queryKey: ['me', 'threads'] });
    },
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subject: string | null; body: string }) =>
      apiFetch<Thread>('/me/threads', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'threads'] });
    },
  });
}

/**
 * Mark a thread read for the current patient. Idempotent — safe to call
 * on every ThreadDetail mount. Invalidates the threads list on success
 * so the unread badge clears immediately.
 */
export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch<void>(`/me/threads/${threadId}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'threads'] });
    },
  });
}

export function usePatient() {
  return useQuery({
    queryKey: ['me', 'patient'],
    queryFn: () => apiFetch<Patient>('/me/patient'),
    staleTime: 5 * 60_000,
  });
}

export function useUpdatePatientAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAddressBody) =>
      apiFetch<Patient>('/me/patient/address', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'patient'] });
      qc.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

/** Body for `PATCH /me/patient`. DOB and sex are accepted only while the
 * chart has none; changing a recorded value is a 409
 * `contact_care_team_to_change`. */
export type UpdatePatientBody = {
  timezone?: string | null;
  phone?: string;
  date_of_birth?: string;
  sex?: 'male' | 'female' | 'other';
};

/**
 * Patch the patient's profile: the /account timezone dropdown, the
 * Account profile editor, and the revert path from the auto-sync toast.
 */
export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePatientBody) =>
      apiFetch<Patient>('/me/patient', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'patient'] });
    },
  });
}

/**
 * Push the browser-detected IANA timezone to the backend so Healthie
 * and our DB mirror stay in sync with where the patient actually is.
 * Server short-circuits when the value already matches, so this is
 * safe to call on every portal load. Returns `changed=true` when the
 * value moved, which the caller uses to surface the revertable toast.
 */
export function useSyncTimezone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (browser_timezone: string) =>
      apiFetch<TimezoneSyncResponse>('/me/timezone/sync', {
        method: 'POST',
        body: JSON.stringify({ browser_timezone }),
      }),
    onSuccess: (data) => {
      if (data.changed) {
        qc.invalidateQueries({ queryKey: ['me', 'patient'] });
      }
    },
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      apiFetch<CancelOrderResponse>(
        `/me/orders/${encodeURIComponent(orderId)}/cancel`,
        { method: 'POST' }
      ),
    onSuccess: (_, orderId) => {
      qc.invalidateQueries({ queryKey: ['me', 'orders'] });
      qc.invalidateQueries({ queryKey: ['me', 'order', orderId] });
      qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] });
    },
  });
}

export function useProtocolsCatalog() {
  return useQuery({
    queryKey: ['protocols', 'catalog'],
    queryFn: () => apiFetch<ProtocolSummary[]>('/protocols/catalog'),
    staleTime: 5 * 60_000,
  });
}

export function useLearnIndex() {
  return useQuery({
    queryKey: ['content', 'learn'],
    queryFn: () => apiFetch<ArticleCard[]>('/content/learn'),
    staleTime: 5 * 60_000,
  });
}

export function useLearningResources(enabled = true) {
  return useQuery({
    queryKey: ['content', 'resources'],
    queryFn: () => apiFetch<LearningResource[]>('/content/resources'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useGuide(slug: string | undefined) {
  return useQuery({
    queryKey: ['content', 'guide', slug],
    queryFn: () => apiFetch<Guide>(`/content/guides/${slug}`),
    enabled: Boolean(slug),
    staleTime: 10 * 60_000,
  });
}

export function useArticle(slug: string | undefined) {
  return useQuery({
    queryKey: ['content', 'article', slug],
    queryFn: () => apiFetch<Article>(`/content/articles/${slug}`),
    enabled: Boolean(slug),
    staleTime: 10 * 60_000,
  });
}

export function useMarketplace(enabled = true) {
  return useQuery({
    queryKey: ['marketplace'],
    queryFn: () => apiFetch<MarketplaceResponse>('/marketplace'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export interface SuggestedCartLine {
  product_id: string;
  quantity: number;
}

export interface SuggestedCart {
  id: string;
  lines: SuggestedCartLine[];
  note: string | null;
}

const SUGGESTED_CART_KEY = ['me', 'suggested-cart'];

// An admin-assigned product pre-selection (patient profile builder).
// Reading it doesn't consume it: AppLayout adds the lines to the cart, then
// claims it with useClaimSuggestedCart. A cart assigned during an open
// session shows up when the patient returns to the tab after the stale
// time, without background polling (the app disables focus refetch by
// default).
export function useSuggestedCart() {
  return useQuery({
    queryKey: SUGGESTED_CART_KEY,
    queryFn: () => apiFetch<SuggestedCart | null>('/me/suggested-cart'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useClaimSuggestedCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cartId: string) =>
      apiFetch<{ claimed: boolean }>(`/me/suggested-cart/${cartId}/claim`, {
        method: 'POST',
      }),
    // Claimed (here or in another tab): nothing left to hydrate. Only clear
    // the cache if it still holds this cart; a newer suggestion that arrived
    // while the claim was in flight must survive.
    onSuccess: (_result, cartId) =>
      qc.setQueryData<SuggestedCart | null>(SUGGESTED_CART_KEY, (current) =>
        current?.id === cartId ? null : current,
      ),
  });
}

export function useTreatmentOffers() {
  return useQuery({
    queryKey: ['me', 'treatment-offers'],
    queryFn: () => apiFetch<TreatmentOffersResponse>('/me/treatment-offers'),
    staleTime: 30_000,
  });
}

// ---------- Support chat (Plain) ----------

// 503 from the backend means "Plain not configured" — render no widget.
// We swallow that case as `null` so the hook never throws to the boundary.
export interface PlainChatToken {
  app_id: string;
  email: string;
  email_hash: string;
  full_name: string;
  short_name: string;
}

export function usePlainChatToken() {
  return useQuery<PlainChatToken | null>({
    queryKey: ['support', 'plain', 'chat-token'],
    queryFn: async () => {
      try {
        return await apiFetch<PlainChatToken>('/me/support/chat-token');
      } catch (err) {
        if (
          err instanceof Error &&
          'status' in err &&
          (err as { status?: number }).status === 503
        ) {
          return null;
        }
        throw err;
      }
    },
    // Hash is bound to the patient's email and our server-side secret.
    // It doesn't change unless the email changes; refetch infrequently.
    staleTime: 30 * 60_000,
  });
}

// ---------- Care requests ----------

export interface CareRequestItemOut {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  price_cents: number;
  quantity: number;
  origin: 'product' | 'protocol';
  image_url?: string | null;
}

export interface CareRequestOut {
  id: string;
  status: string;
  thread_id: string | null;
  subtotal_cents: number;
  note: string | null;
  items: CareRequestItemOut[];
  created_at: string;
}

export function useSubmitCareRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { items: CareRequestItemOut[]; note?: string | null }) =>
      apiFetch<CareRequestOut>('/me/care-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'threads'] });
      qc.invalidateQueries({ queryKey: ['me', 'care-requests'] });
    },
  });
}

// ---------- Direct marketplace purchase (cart → saved-card subscription) ----------
// Replaces useSubmitCareRequest for the new flow: charges the patient's
// saved Stripe card off-session and creates per-line awaiting_prescription
// rows with the same required_intake_form_id gating as lander purchases.

export interface MarketplacePurchaseInItem {
  cart_promotion_line_key?: string;
  sanity_product_id: string;
  frequency?: 'monthly' | 'quarterly' | 'sixMonth' | 'twelveMonth';
  /** Clinician-authored, patient-owned offer that permits this exact product
   *  and medication tier to bypass repeat intake/sync/Rx review gates. */
  clinical_authorization_id?: string;
  dose_offer_id?: string;
  dose_offer_version?: string;
  // Lifecycle marketing attribution — item-level, metadata-only (backend
  // stamps it on Stripe + pharmacy-order metadata for analytics).
  lifecycle_campaign_key?: string;
  lifecycle_message_key?: string;
  lifecycle_recommendation_id?: string;
}

export interface MarketplacePurchaseResultItem {
  pharmacy_order_id: string;
  subscription_id: string;
  canonical_product_id: string;
  intake_required: boolean;
  intake_form_id: string | null;
  needs_consent: boolean;
}

export function useMarketplacePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      items: MarketplacePurchaseInItem[];
      purchase_request_id?: string;
      cart_promotion_id?: string;
      // Optional: charge a specific saved card the patient picked at
      // checkout. Omitted → backend uses the customer's default card.
      payment_method_id?: string;
      // An Apple Pay sheet the patient just approved: the first line is paid
      // with it while they're present (instead of payment_method_id).
      wallet_payment_method_id?: string;
      // Optional Stripe promotion code — the backend re-validates it
      // against Stripe and applies it to the created subscription(s).
      promo_code?: string;
    }) =>
      apiFetch<{ items: MarketplacePurchaseResultItem[] }>(
        '/me/marketplace/purchase',
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'treatment-offers'] });
      void qc.invalidateQueries({ queryKey: ['home'] });
      void qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] });
      void qc.invalidateQueries({ queryKey: ['me', 'orders'] });
    },
  });
}

/** Complete only an authorization's saved payment attempt. The server uses
 * frozen payment/catalog details; never send a replacement card or plan here. */
export function useResumeTreatmentOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (authorizationId: string) =>
      apiFetch<{ items: MarketplacePurchaseResultItem[] }>(
        `/me/marketplace/clinical-offers/${encodeURIComponent(authorizationId)}/resume`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'treatment-offers'] });
      void qc.invalidateQueries({ queryKey: ['home'] });
      void qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] });
      void qc.invalidateQueries({ queryKey: ['me', 'orders'] });
    },
  });
}

// ---------- Promo codes ----------

export interface PromoCodeValidation {
  valid: boolean;
  code: string;
  discount_cents: number;
  discount_label: string | null;
  // Raw coupon terms so carts can re-preview the discount when the
  // subtotal changes without re-calling the endpoint.
  percent_off: number | null;
  amount_off_cents: number | null;
  // not_found | inactive | expired | currency_mismatch | no_discount
  reason: string | null;
}

/** Client-side discount preview for a subtotal, mirroring Stripe's math. */
export function promoDiscountCents(
  promo: PromoCodeValidation | null | undefined,
  subtotalCents: number
): number {
  if (!promo?.valid) return 0;
  if (promo.percent_off) {
    return Math.round((subtotalCents * promo.percent_off) / 100);
  }
  return Math.min(promo.amount_off_cents ?? 0, subtotalCents);
}

/** Friendly copy for a failed validation reason. */
export function promoErrorCopy(reason: string | null | undefined): string {
  switch (reason) {
    case 'expired':
      return 'That code has expired.';
    case 'inactive':
      return 'That code is no longer active.';
    case 'already_used':
      return 'This code has already been used on your account.';
    case 'first_time_only':
      return 'This code is only valid for first-time customers. Remove it to continue.';
    case 'first_time_single_item':
      return 'This first-time customer code can only be used with one treatment.';
    case 'customer_ineligible':
      return "This code isn't available for your account. Remove it to continue.";
    case 'lookup_failed':
      return "We couldn't verify this code right now. Please try again.";
    default:
      return "That code doesn't look right — check it and try again.";
  }
}

/**
 * Validate a Stripe promotion code + preview its discount. Authed
 * endpoint: the same Stripe lookup the lander uses PLUS the
 * once-per-customer check (`reason: 'already_used'`). Doesn't consume a
 * redemption — the apply happens at purchase time, where the backend
 * re-enforces the policy regardless.
 */
export function useValidatePromoCode() {
  return useMutation({
    mutationFn: (body: { code: string; amount_cents: number }) =>
      apiFetch<PromoCodeValidation>('/me/validate-promo-code', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

// ---------- Lifecycle marketing events ----------

export interface LifecycleEventIn {
  campaign_key: string;
  message_key?: string | null;
  recommendation_id?: string | null;
  event_type:
    | 'email_clicked'
    | 'modal_viewed'
    | 'add_to_cart'
    | 'purchase_started';
  metadata?: Record<string, unknown>;
}

export function useLifecycleEvent() {
  return useMutation({
    mutationFn: (body: LifecycleEventIn) =>
      apiFetch<{ ok: boolean }>('/lifecycle/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

// ---------- Payment methods (saved cards) ----------

export function usePaymentMethods(enabled = true) {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['me', 'payment-methods', session?.user.id],
    queryFn: () => apiFetch<PaymentMethodsResponse>('/me/payment-methods'),
    enabled: enabled && Boolean(session?.user.id),
    staleTime: 60_000,
  });
}

export function useCreateSetupIntent() {
  return useMutation({
    mutationFn: () =>
      apiFetch<SetupIntentResponse>('/me/payment-methods/setup-intent', {
        method: 'POST',
      }),
  });
}

export function useSetDefaultPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pmId: string) =>
      apiFetch<PaymentMethodsResponse>(
        `/me/payment-methods/${encodeURIComponent(pmId)}/default`,
        { method: 'POST' }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'payment-methods'] });
    },
  });
}

export function useDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pmId: string) =>
      apiFetch<PaymentMethodsResponse>(
        `/me/payment-methods/${encodeURIComponent(pmId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'payment-methods'] });
    },
  });
}

// ---------- Onboarding + intake ----------

export interface OnboardingStatus {
  onboarding_state:
    | 'signed_up'
    | 'intake_in_progress'
    | 'intake_submitted'
    | 'scheduling_required'
    | 'complete';
  intake_required: boolean;
  intake_submitted_at: string | null;
}

export function useOnboarding() {
  return useQuery({
    queryKey: ['me', 'onboarding'],
    queryFn: () => apiFetch<OnboardingStatus>('/me/onboarding'),
    staleTime: 30_000,
  });
}

export function useIntakeDraft(
  formSlug: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['me', 'intake', 'draft', formSlug],
    queryFn: () =>
      apiFetch<{
        form_slug: string;
        answers: Record<string, unknown>;
        updated_at: string | null;
      }>(`/me/intake/draft/${formSlug}`),
    enabled,
    staleTime: 10_000,
  });
}

export function useSaveIntakeDraft(formSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answers: Record<string, unknown>) =>
      apiFetch(`/me/intake/draft/${formSlug}`, {
        method: 'PUT',
        body: JSON.stringify({ answers }),
      }),
    // Don't invalidate the draft query — the wizard is authoritative for local
    // state while the user is typing. Refetching the draft on every save causes
    // the server echo to race in-flight keystrokes and "flash" the input.
    // Prime the cache so future reads (e.g. remount) see the latest value.
    onSuccess: (_data, vars) => {
      qc.setQueryData(['me', 'intake', 'draft', formSlug], (prev: unknown) => ({
        form_slug: formSlug,
        answers: vars,
        updated_at:
          (prev as { updated_at?: string } | undefined)?.updated_at ??
          new Date().toISOString(),
      }));
      qc.invalidateQueries({ queryKey: ['me', 'onboarding'] });
    },
  });
}

export function useSubmitIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      form_slug: string;
      answers: Record<string, unknown>;
      healthie_form_mapping?: {
        healthie_form_id: string;
        questions: Record<string, string>;
      };
    }) =>
      apiFetch<OnboardingStatus>('/me/intake/submit', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'onboarding'] });
      qc.invalidateQueries({ queryKey: ['bootstrap'] });
      qc.invalidateQueries({ queryKey: ['me', 'required-intakes'] });
      qc.invalidateQueries({ queryKey: ['me', 'renewal-checkins'] });
    },
  });
}

// ---------- Required intakes (per-product Healthie forms) ----------
// Patients must complete a product-specific Healthie intake before Dr. Hinds
// writes the script. /me/required-intakes lists the per-line awaiting orders
// that still need the intake done; once submitted the row is removed from
// the list and the gated write-Rx tasks fire.

export interface RequiredIntake {
  awaiting_order_id: string;
  product_id: string;
  product_name: string;
  intake_kind: 'treatment_intake' | 'renewal_checkin' | 'intake_correction';
  intake_label: string;
  healthie_form_id: string;
  intake_submitted_at: string | null;
  created_at: string;
  /** Set when a clinician asked for a new intake because the answers looked wrong. */
  correction_reason_label?: string | null;
}

export interface RenewalCheckinTask {
  id: string;
  product_id: string;
  product_name: string;
  healthie_form_id: string;
  renewal_at: string;
}

export function useRenewalCheckins() {
  const { session, loading } = useAuth();
  return useQuery({
    queryKey: ['me', 'renewal-checkins', session?.user.id],
    queryFn: () => apiFetch<{ items: RenewalCheckinTask[]; unavailable: boolean }>(
      '/me/renewal-checkins',
    ),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    enabled: !loading && Boolean(session),
  });
}

export function useRequiredIntakes(opts?: {
  /** Poll while a caller is waiting for a just-purchased product's intake
   *  row to land (order processing can trail the patient's portal entry by
   *  a few seconds). */
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: ['me', 'required-intakes'],
    queryFn: () => apiFetch<RequiredIntake[]>('/me/required-intakes'),
    staleTime: 30_000,
    refetchInterval: opts?.refetchInterval ?? false,
  });
}

export interface HealthieFormModule {
  id: string;
  label: string;
  mod_type: string;
  required: boolean;
  options: string;
  /** Exact server-authored dependency; absent on existing forms. */
  visible_when?: { module_id: string; equals: string[] } | null;
  disqualifying_values?: string[];
  disqualification_message?: string | null;
  exclusive_options?: string[];
}

export interface HealthieForm {
  id: string;
  name: string;
  modules: HealthieFormModule[];
  eligibility_message?: string | null;
}

export function useHealthieForm(
  formId: string | null | undefined,
  opts?: { personalize?: boolean },
) {
  const personalize = opts?.personalize ?? true;
  return useQuery({
    queryKey: ['me', 'healthie-form', formId, { personalize }],
    queryFn: () => apiFetch<HealthieForm>(
      `/me/intake/healthie-form/${formId}?personalize=${personalize}`,
    ),
    enabled: !!formId,
    staleTime: 10 * 60_000,
  });
}

export interface IntakeFormForProduct {
  product_id: string;
  healthie_form_id: string;
}

// Resolve a product → its Healthie intake form id WITHOUT an awaiting-order
// row. Powers the pre-charge renewal intake (ProductIntake): the 5-day-before-
// renewal notice links to /app/intake?product=<id> before any awaiting row
// exists, so we resolve the form straight from the product. 404 = the product
// has no intake gate (don't retry — there's nothing to complete).
export function useIntakeFormForProduct(productId: string | null | undefined) {
  return useQuery({
    queryKey: ['me', 'intake-form-for-product', productId],
    queryFn: () =>
      apiFetch<IntakeFormForProduct>(
        `/me/intake/form-for-product/${productId}`
      ),
    enabled: !!productId,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

// Submit a Healthie-form intake. Answers are keyed by module_id (since the
// portal reads modules straight from Healthie). The mapping passed to the
// backend uses an identity {module_id: module_id} so the existing
// intake_service.submit_intake plumbing routes the answers into Healthie's
// createFormAnswerGroup unchanged.
export function useSubmitHealthieIntake() {
  const submit = useSubmitIntake();
  return {
    ...submit,
    mutate: (vars: {
      healthie_form_id: string;
      answers: Record<string, string>;
    }) => {
      const questions = Object.fromEntries(
        Object.keys(vars.answers).map((k) => [k, k])
      );
      return submit.mutate({
        form_slug: `healthie-${vars.healthie_form_id}`,
        answers: vars.answers,
        healthie_form_mapping: {
          healthie_form_id: vars.healthie_form_id,
          questions,
        },
      });
    },
    mutateAsync: (vars: {
      healthie_form_id: string;
      answers: Record<string, string>;
    }) => {
      const questions = Object.fromEntries(
        Object.keys(vars.answers).map((k) => [k, k])
      );
      return submit.mutateAsync({
        form_slug: `healthie-${vars.healthie_form_id}`,
        answers: vars.answers,
        healthie_form_mapping: {
          healthie_form_id: vars.healthie_form_id,
          questions,
        },
      });
    },
  };
}

// ---------- Subscription lifecycle (patient-facing) ----------

export interface UpcomingFill {
  dose_contents?: string | null;
  id: string;
  cycle_index: number;
  shipment_index: number;
  total_shipments: number;
  scheduled_for: string;
  status: string;
  container_qty: number;
}

export interface MeSubscription {
  dose_contents?: string | null;
  stripe_subscription_id: string;
  product_id: string | null;
  product_name: string | null;
  status:
    | 'active'
    | 'paused'
    | 'cancel_at_period_end'
    | 'cancelled'
    | 'payment_failed';
  /** True for recoverable past_due and terminal unpaid billing states. */
  past_due?: boolean;
  /** Server-authoritative duplicate-purchase decision. Optional during a
   *  staggered backend/frontend deploy; callers fall back to status. */
  blocks_repurchase?: boolean;
  plan_cycles: number;
  current_period_end: string | null;
  next_renewal_at: string | null;
  cancel_at_period_end: boolean;
  paused: boolean;
  pause_resumes_at: string | null;
  /** False for Stripe-native pauses that require billing/support recovery. */
  can_self_resume?: boolean;
  unit_amount_cents: number | null;
  /** What the NEXT invoice actually charges after active discounts
   *  (e.g. the cancellation-save 25% coupon). Null when no discount. */
  next_renewal_amount_cents?: number | null;
  currency: string | null;
  upcoming_fills: UpcomingFill[];
}

export interface SubscriptionsListResponse {
  subscriptions: MeSubscription[];
}

export interface SubscriptionActionResponse {
  stripe_subscription_id: string;
  status: string;
  next_renewal_at: string | null;
  paused: boolean;
  pause_resumes_at: string | null;
  cancel_at_period_end: boolean;
}

export function useMySubscriptions() {
  // Gate on the AuthProvider's session so the request never goes out
  // before the Supabase token is restored from storage. Without this
  // the very first mount can fire before getSession() has resolved,
  // producing a 401 missing_bearer that React Query then caches as a
  // failure for 30 seconds — masking that the session is actually fine.
  const { session, loading } = useAuth();
  return useQuery({
    queryKey: ['me', 'subscriptions'],
    queryFn: () => apiFetch<SubscriptionsListResponse>('/me/subscriptions'),
    staleTime: 30_000,
    enabled: !loading && !!session,
    retry: (failureCount, err) => {
      // Auto-retry once on 401 — handles the race where the session
      // refreshes mid-request. After that, surface the error.
      const status = (err as { status?: number } | undefined)?.status;
      if (status === 401 && failureCount < 1) return true;
      return failureCount < 1;
    },
  });
}

function invalidateSubs(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['me', 'renewal-checkins'] });
  qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'me' && q.queryKey[1] === 'subscriptions',
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subId }: { subId: string }) =>
      apiFetch<SubscriptionActionResponse>(
        `/me/subscriptions/${subId}/cancel`,
        {
          method: 'POST',
        }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

// ---- cancellation-save flow (the reason dialog behind Cancel) ----

export type CancelFlowReason =
  | 'too_expensive'
  | 'enough_supply'
  | 'never_arrived'
  | 'not_working'
  | 'dislike_treatment'
  | 'reached_goal'
  | 'taking_break'
  | 'stopped_therapy'
  | 'switched_provider'
  | 'side_effects'
  | 'prescription_issue'
  | 'communication_issue'
  | 'arrived_late'
  | 'arrived_hot_or_damaged'
  | 'other';

export type CancelFlowOrderState =
  | 'intake_required'
  | 'consult_required'
  | 'awaiting_prescription'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'unknown';

export interface CancelFlowOrderContext {
  state: CancelFlowOrderState;
  order_number?: string | null;
  order_status?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  tracking_number?: string | null;
  tracking_url?: string | null;
}

export interface CancelFlowStart {
  attempt_id: string;
  offer:
    | 'discount_25'
    | 'education_discount_25'
    | 'pause'
    | 'order_status'
    | 'none';
  discount_percent: number | null;
  renewal_price_cents: number | null;
  discounted_price_cents: number | null;
  next_renewal_at: string | null;
  discount_already_used: boolean;
  pause_options_days: number[];
  order_context: CancelFlowOrderContext | null;
  product_name: string | null;
}

export function useStartCancelFlow() {
  return useMutation({
    mutationFn: ({
      subId,
      reason,
      reasonText,
    }: {
      subId: string;
      reason: CancelFlowReason;
      reasonText?: string;
    }) =>
      apiFetch<CancelFlowStart>(`/me/subscriptions/${subId}/cancel-flow`, {
        method: 'POST',
        body: JSON.stringify({
          reason,
          reason_schema_version: 2,
          reason_text: reasonText || undefined,
        }),
      }),
  });
}

export interface CancelFlowDiscountResult {
  attempt_id: string;
  discount_percent: number;
  renewal_price_cents: number | null;
  discounted_price_cents: number | null;
  next_renewal_at: string | null;
}

export function useAcceptCancelFlowDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subId, attemptId }: { subId: string; attemptId: string }) =>
      apiFetch<CancelFlowDiscountResult>(
        `/me/subscriptions/${subId}/cancel-flow/${attemptId}/accept-discount`,
        { method: 'POST' }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

export function useConfirmCancelFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subId, attemptId }: { subId: string; attemptId: string }) =>
      apiFetch<SubscriptionActionResponse>(
        `/me/subscriptions/${subId}/cancel-flow/${attemptId}/confirm-cancel`,
        { method: 'POST' }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

export function useReportCancelFlowOutcome() {
  return useMutation({
    mutationFn: ({
      subId,
      attemptId,
      outcome,
      days,
    }: {
      subId: string;
      attemptId: string;
      outcome: 'saved_pause' | 'abandoned';
      days?: number;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/me/subscriptions/${subId}/cancel-flow/${attemptId}/outcome`,
        { method: 'POST', body: JSON.stringify({ outcome, days }) }
      ),
  });
}

export function useSubmitCancelFlowFeedback() {
  return useMutation({
    mutationFn: ({
      subId,
      attemptId,
      text,
    }: {
      subId: string;
      attemptId: string;
      text: string;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/me/subscriptions/${subId}/cancel-flow/${attemptId}/feedback`,
        { method: 'POST', body: JSON.stringify({ text }) }
      ),
  });
}

export function usePauseSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subId, days }: { subId: string; days: number }) =>
      apiFetch<SubscriptionActionResponse>(`/me/subscriptions/${subId}/pause`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }),
    onSuccess: () => invalidateSubs(qc),
  });
}

export function useResumeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subId }: { subId: string }) =>
      apiFetch<SubscriptionActionResponse>(
        `/me/subscriptions/${subId}/resume`,
        {
          method: 'POST',
        }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

export function useRescheduleSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subId,
      nextRenewalDate,
    }: {
      subId: string;
      nextRenewalDate: string;
    }) =>
      apiFetch<SubscriptionActionResponse>(
        `/me/subscriptions/${subId}/reschedule`,
        {
          method: 'POST',
          body: JSON.stringify({ next_renewal_date: nextRenewalDate }),
        }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

export type PlanFrequencyOption = 'monthly' | 'quarterly' | 'sixMonth';

export interface SubscriptionPlanOption {
  frequency: PlanFrequencyOption;
  label: string;
  interval_days: number;
  renewal_cents: number;
  savings_vs_monthly_cents: number | null;
  is_current: boolean;
}

export interface SubscriptionPlanOptionsResponse {
  stripe_subscription_id: string;
  product_id: string | null;
  current_frequency: PlanFrequencyOption | null;
  current_renewal_cents: number | null;
  effective_at: string | null;
  options: SubscriptionPlanOption[];
}

// ── Marketplace format switch (e.g. NAD injection ↔ NAD nasal) ────────────────

export interface SwitchFormatQuote {
  eligible: boolean;
  reason: string | null;
  /** "immediate" = pending order swaps now; "next_renewal" = current
   *  cycle already shipped, switch lands at the next renewal. */
  mode?: 'immediate' | 'next_renewal' | null;
  effective_at?: string | null;
  order_id: string | null;
  order_number: string | null;
  from_product_id: string | null;
  from_product_name: string | null;
  to_product_id: string | null;
  to_product_name: string | null;
  due_today_cents: number;
  new_recurring_cents: number | null;
  new_interval_days: number | null;
  cycle_term_days: number | null;
  first_cycle: boolean;
  intake_may_be_required: boolean;
}

export interface SwitchFormatResult {
  mode?: 'immediate' | 'next_renewal';
  effective_at?: string | null;
  order_id: string | null;
  order_number: string | null;
  from_product: string | null;
  to_product: string | null;
  intake_regated: boolean;
  due_paid_cents: number;
  fulfillment_outcome: string | null;
}

/** Preview what switching the patient's pending treatment to a different
 *  format of the same chemical would cost. Fetched when the modal opens. */
export function useSwitchFormatQuote(toCanonical: string | null) {
  return useQuery({
    queryKey: ['marketplace', 'switch-format', 'quote', toCanonical],
    queryFn: () =>
      apiFetch<SwitchFormatQuote>(
        `/me/marketplace/switch-format/quote?to=${encodeURIComponent(toCanonical!)}`
      ),
    enabled: Boolean(toCanonical),
    staleTime: 30_000,
  });
}

/** Apply the format switch — same backend machinery as the admin tool and
 *  the clinician confirm-task, patient-initiated. */
export function useSwitchFormat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { to_canonical_product_id: string }) =>
      apiFetch<SwitchFormatResult>('/me/marketplace/switch-format', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      for (const key of [
        ['marketplace'],
        ['orders'],
        ['home'],
        ['me', 'required-intakes'],
        ['me', 'renewal-checkins'],
        ['me', 'clinical-state'],
        ['me', 'subscriptions'],
      ]) {
        qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}

/** Terms this sub's product can be billed at — fetched on demand when the
 *  patient opens the "Switch plan length" panel. */
export function useSubscriptionPlanOptions(subId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['me', 'subscriptions', subId, 'plan-options'],
    queryFn: () =>
      apiFetch<SubscriptionPlanOptionsResponse>(
        `/me/subscriptions/${subId}/plan-options`
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Switch plan length (e.g. monthly → quarterly). Takes effect at the next
 *  renewal — the current cycle's billing and fills are untouched. */
export function useChangeSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subId,
      frequency,
      source,
      experimentKey,
      variantKey,
    }: {
      subId: string;
      frequency: PlanFrequencyOption;
      // Attribution for surfaces beyond Account → Subscriptions (e.g. the
      // post-purchase quarterly upsell step). Stamped on sub metadata +
      // the backend PostHog event; never affects billing.
      source?: string;
      experimentKey?: string;
      variantKey?: string;
    }) =>
      apiFetch<SubscriptionActionResponse>(
        `/me/subscriptions/${subId}/change-plan`,
        {
          method: 'POST',
          body: JSON.stringify({
            frequency,
            ...(source ? { source } : {}),
            ...(experimentKey ? { experiment_key: experimentKey } : {}),
            ...(variantKey ? { variant_key: variantKey } : {}),
          }),
        }
      ),
    onSuccess: () => invalidateSubs(qc),
  });
}

// ── Support chat ───────────────────────────────────────────────────────────────

export function useSupportThread() {
  return useQuery({
    queryKey: ['support', 'thread'],
    queryFn: () => apiFetch<SupportThread>('/me/support/thread'),
    staleTime: 30_000,
    refetchInterval: 30_000, // polling backstop if Realtime drops
  });
}

export function useSendSupportMessage() {
  const qc = useQueryClient();
  const getThreadQuery = () => qc.getQueryCache().find({ queryKey: ['support', 'thread'], exact: true });
  return useMutation({
    mutationFn: async (message: string | { body: string; attachment_ids: string[] }) => {
      await flushCartContext();
      const { body, attachment_ids } =
        typeof message === 'string' ? { body: message, attachment_ids: [] } : message;
      return apiFetch<SendMessageResponse>('/me/support/messages', {
        method: 'POST',
        body: JSON.stringify(attachment_ids.length ? { body, attachment_ids } : { body }),
      });
    },
    onMutate: (message) => {
      const threadQuery = getThreadQuery();
      const body = typeof message === 'string' ? message : message.body;
      // Optimistically add the patient's message immediately
      const optimistic: SupportMessage = {
        id: `optimistic-${Date.now()}`,
        sender_role: 'patient',
        sender_name: null,
        body,
        created_at: new Date().toISOString(),
      };
      const prev = qc.getQueryData<SupportThread>(['support', 'thread']);
      qc.setQueryData<SupportThread>(['support', 'thread'], (p) => {
        if (!p) return p;
        // Sending a message reopens the chat (mirrors the backend's reopen):
        // drop the resolved flag + rating so the "Thanks for your feedback"
        // banner clears instantly instead of waiting for Realtime/refetch.
        // Leave `escalated` alone — a human may still be handling the thread,
        // matching the backend, which only reopens when not escalated.
        const reopened = p.escalated
          ? {}
          : { resolved: false, feedback_rating: null };
        return { ...p, ...reopened, messages: [...p.messages, optimistic] };
      });
      return {
        threadQuery,
        optimisticId: optimistic.id,
        prevResolved: prev?.resolved ?? false,
        prevFeedback: prev?.feedback_rating ?? null,
      };
    },
    onSuccess: (data, _body, context) => {
      // AuthProvider clears this query on identity changes, but a pending
      // mutation still settles after clear, even if its sender unmounted.
      if (!context?.threadQuery || context.threadQuery !== getThreadQuery()) return;
      // Replace optimistic + any Realtime-delivered copies with server messages
      qc.setQueryData<SupportThread>(['support', 'thread'], (prev) => {
        if (!prev) return prev;
        const incomingIds = new Set(
          [data.patient_message.id, data.agent_message?.id].filter(Boolean)
        );
        const filtered = prev.messages.filter(
          (m) => m.id !== context?.optimisticId && !incomingIds.has(m.id)
        );
        const newMsgs: SupportMessage[] = [data.patient_message];
        if (data.agent_message) newMsgs.push(data.agent_message);
        return { ...prev, messages: [...filtered, ...newMsgs] };
      });
    },
    onError: (_err, _body, context) => {
      if (!context?.threadQuery || context.threadQuery !== getThreadQuery()) return;
      // Roll back the optimistic message AND the optimistic reopen on failure.
      qc.setQueryData<SupportThread>(['support', 'thread'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          resolved: context?.prevResolved ?? prev.resolved,
          feedback_rating: context?.prevFeedback ?? prev.feedback_rating,
          messages: prev.messages.filter((m) => m.id !== context?.optimisticId),
        };
      });
    },
  });
}

// ---------- Care-team messaging ----------

/** AI check before a Providers send: is this really an ops question? Fails
 * open — the backend answers `suggest_team: null` on any classifier error. */
export function useMessagingRouteCheck() {
  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<MessagingRouteResponse>('/me/messaging/route', {
        method: 'POST',
        body: JSON.stringify({ text, team: 'providers' }),
      }),
  });
}

/** The patient's care team (physician + nurse). A nurse only joins the Healthie
 * conversation once she posts, so the thread's participants undercount it. */
export function useCareTeam(enabled: boolean) {
  return useQuery({
    queryKey: ['me', 'care-team'],
    enabled,
    queryFn: async () => {
      try {
        return await apiFetch<{ providers: CareTeamProvider[] }>('/me/care-team');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return { providers: [] };
        throw err;
      }
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** Support tickets as read-only status. A backend without the route (404)
 * reads as "no requests" so the portal can ship ahead of it. */
export function useSupportRequests(enabled: boolean) {
  return useQuery({
    queryKey: ['support', 'requests'],
    enabled,
    queryFn: async () => {
      try {
        return await apiFetch<SupportRequestsResponse>('/me/support/requests');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { requests: [], expected_reply: null } satisfies SupportRequestsResponse;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useMarkSupportRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>('/me/support/mark-read', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData<SupportThread>(['support', 'thread'], (prev) =>
        prev ? { ...prev, unread_count: 0 } : prev
      );
    },
  });
}

export function useRealtimeSupportMessages(threadId: string | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!threadId) return;

    const channel = supabase
      .channel(`support_messages:${threadId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          const msg = payload.new as SupportMessage;
          qc.setQueryData<SupportThread>(['support', 'thread'], (prev) => {
            if (!prev) return prev;
            // Avoid exact duplicates (e.g. onSuccess already committed this id)
            if (prev.messages.some((m) => m.id === msg.id)) return prev;
            // Patient message: replace the optimistic placeholder rather than
            // appending alongside it (which causes visible duplication until
            // onSuccess fires after the AI responds).
            const base =
              msg.sender_role === 'patient'
                ? prev.messages.filter((m) => !m.id.startsWith('optimistic-'))
                : prev.messages;
            const unread =
              msg.sender_role !== 'patient'
                ? prev.unread_count + 1
                : prev.unread_count;
            return {
              ...prev,
              messages: [...base, msg],
              unread_count: unread,
            };
          });
        }
      )
      .on(
        // Escalation toggles on/off via support_threads.escalated_at (no message
        // is inserted), so we track it here to flip the human-agent banner
        // instantly — e.g. when a teammate marks the Plain thread Done.
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_threads',
          filter: `id=eq.${threadId}`,
        },
        (payload) => {
          const row = payload.new as {
            escalated_at: string | null;
            resolved_at: string | null;
            feedback_rating: 'up' | 'down' | null;
          };
          qc.setQueryData<SupportThread>(['support', 'thread'], (prev) =>
            prev
              ? {
                  ...prev,
                  escalated: Boolean(row.escalated_at),
                  resolved: Boolean(row.resolved_at),
                  feedback_rating: row.feedback_rating ?? null,
                }
              : prev
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, qc]);
}

export const nativeFeedMissing = (error: unknown) => error instanceof ApiError && error.status === 404;

/** Native requests share the rep inbox's ticket IDs; never query legacy cases. */
export function useNativePatientRequests(enabled: boolean) {
  const { session } = useAuth();
  return useInfiniteQuery({
    queryKey: ['support', 'native-requests', session?.user.id],
    initialPageParam: null as NativePageCursor | null,
    queryFn: ({ pageParam, signal }) => apiFetch<NativeRequests>(nativePagePath(pageParam), { signal }),
    getNextPageParam: nextNativePage,
    select: (data) => mergeNativePages(data.pages),
    enabled: (query) => enabled && !!session?.user.id && !nativeFeedMissing(query.state.error),
    staleTime: 5_000,
    gcTime: enabled ? Infinity : undefined,
    retry: (count, error) => !nativeFeedMissing(error) && count < 3,
    refetchInterval: 10_000,
  });
}
